import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { UmraPackagesService, UmraPackageProperties, Hotels } from '../Services/umra-packages.service';
import {
  faHotel,
  faListSquares,
  faCheckCircle,
  faFile,
  faCalendar,
  faClockRotateLeft,
  faPlane,
  faPlaneDeparture,
  faLocationDot,
  faLocationArrow,
  faEye,
  faKaaba,
  faRoute,
  faPlusCircle,
  faFileCirclePlus,
  faExclamationTriangle,
  faCopy,
  faTag,
  faMap,
  faImage,
  faSquareArrowUpRight,
  faInfoCircle,
  faRupeeSign,
  faFileLines,
  faTable,
  faListCheck,
  faCloudUpload,
  faXmarkCircle
} from '@fortawesome/free-solid-svg-icons';
export type PackageMode = 'single' | 'batch' | 'duplicate';
declare var $;
export class UmraFlightSegmentProperties {
  travelDate: string;
  arrivalDate: string;
  airlineName: string;
  routeStart: string;
  routeEnd: string;
  etd: string;
  eta: string;
  flightNo: string;
}

@Component({
  selector: 'app-umra-custom-packages-add',
  templateUrl: './umra-custom-packages-add.component.html',
  styleUrls: ['./umra-custom-packages-add.component.css']
})
export class UmraCustomPackagesAddComponent implements OnInit {

  faHotel = faHotel;
  faDiamond = faListSquares;
  faCheckCircle = faCheckCircle;
  faExclamation = faExclamationTriangle;
  faCalendar = faCalendar;
  faClock = faClockRotateLeft;
  faPlane = faPlane;
  faPlaneDeparture = faPlaneDeparture;
  faLocationDot = faLocationDot;
  faLocationArrow = faLocationArrow;
  faEye = faEye;
  faKaaba = faKaaba;
  faRoute = faRoute;
  faPlusCircle = faPlusCircle;
  faFileCirclePlus = faFileCirclePlus;
  faFile = faFile;
  faCopy = faCopy;
  faTag = faTag;
  faMarker = faMap;
  faImage = faImage;
  faSquareArrowUpRight = faSquareArrowUpRight;
  faInfoCircle = faInfoCircle;
  faRupeeSign = faRupeeSign;
  faFileLines = faFileLines;
  faTable = faTable;
  faListCheck = faListCheck;
  faCloudUpload = faCloudUpload;
  faXmarkCircle = faXmarkCircle;

  // Mode
  currentMode: PackageMode = 'single';

  // Forms
  singlePackageForm: FormGroup;
  batchPackageForm: FormGroup;
  duplicateForm: FormGroup;

  // UI State
  loading = false;
  submitted = false;
  successMessage: string | null = null;
  error: string | null = null;

  // Data
  existingPackages: UmraPackageProperties[] = [];

  // Batch preview data
  batchPreviewData: any[] = [];
  showBatchPreview = false;

  // Common dropdown data
  airlines = ['AirBlue', 'Saudi Airlines', 'Emirates', 'Qatar Airways', 'PIA', 'Turkish Airlines'];
  routes = ['KHI-JED', 'KHI-MED', 'LHE-JED', 'LHE-MED', 'ISB-JED', 'ISB-MED'];
  cities = ['KHI', 'LHE', 'ISB', 'JED', 'MED'];
  hotels = ['BA HARIS OR SIMILAR', 'SHAZA OR SIMILAR', 'BARKA OR SIMILAR', 'MOVENPICK', 'HILTON', 'MARRIOTT'];

  makkahHotels: Hotels[] = [];
  madinahHotels: Hotels[] = [];
  allHotels: Hotels[] = [];

  selectedMakkahHotel: Hotels | null = null;
  selectedMadinahHotel: Hotels | null = null;

  constructor(
    private fb: FormBuilder,
    private packageService: UmraPackagesService
  ) {
    // Single Package Form
    this.singlePackageForm = this.fb.group({
      packageTitle: ['', Validators.required],
      nightsCount: [14, [Validators.required, Validators.min(1)]],
      priceSharing: [0, [Validators.required, Validators.min(0)]],
      priceDouble: [0, [Validators.required, Validators.min(0)]],
      priceTriple: [0, [Validators.required, Validators.min(0)]],
      priceQuad: [0, [Validators.required, Validators.min(0)]],
      discountPerPassenger: [0, [Validators.min(0)]],
      serviceChargesPerPassenger: [0, [Validators.min(0)]],
      availableSeats: [30, [Validators.required, Validators.min(1)]],
      badgeText: [''],
      groupCode: [''],
      packageCode: [''],
      provider: ['Tushmit Travel & Tours'],
      providerType: ['Custom'],
      specialNotes: [''],
      makkahZayarat: [false],
      madinahZayarat: [false],
      taifZayarat: [false],
      //Flight Segments
      outboundFlights: this.fb.array([]),
      returnFlights: this.fb.array([]),
      // Makkah Hotel fields
      makkahHotel: ['', Validators.required],
      makkahHotelAddress: [{ value: '', disabled: false }],
      makkahHotelImage: [''],
      makkahHotelMapLocation: [''],

      // Madinah Hotel fields
      madinahHotel: ['', Validators.required],
      madinahHotelAddress: [{ value: '', disabled: false }],
      madinahHotelImage: [''],
      madinahHotelMapLocation: ['']
    });

    // Batch Package Form
    this.batchPackageForm = this.fb.group({
      batchData: ['', Validators.required],
      defaultNights: [14, Validators.required],
      defaultSeats: [30, Validators.required],
      defaultProvider: ['Custom']
    });

    // Duplicate Form
    this.duplicateForm = this.fb.group({
      sourcePackageId: ['', Validators.required]
    });
  }
  minOutboundSegments = 1;  // Always at least 1 outbound segment
  minReturnSegments = 1;    // Always at least 1 return segment
  maxSegments = 6;          // Maximum total segments
  // Update the getter to access form controls
  get f() {
    return this.singlePackageForm.controls;
  }

  // Specific getters for nested FormArrays
  get outboundFlightSegmentsArray(): FormArray {
    return this.singlePackageForm.get('outboundFlights') as FormArray;
  }

  get returnFlightSegmentsArray(): FormArray {
    return this.singlePackageForm.get('returnFlights') as FormArray;
  }
  // Create a flight segment form group
  createFlightSegment(data?: Partial<UmraFlightSegmentProperties>): FormGroup {
    const d = data || {} as any;
    return this.fb.group({
      airlineName: [d.airlineName || '', Validators.required],
      flightNo: [d.flightNo || '', Validators.required],
      routeStart: [d.routeStart || '', [Validators.required, Validators.maxLength(3)]],
      routeEnd: [d.routeEnd || '', [Validators.required, Validators.maxLength(3)]],
      travelDate: [d.travelDate || '', Validators.required],
      arrivalDate: [d.arrivalDate || '', Validators.required],
      etd: [d.etd || '00:00', Validators.required],
      eta: [d.eta || '00:00', Validators.required]
    });
  }
  // Add outbound segment
  addOutboundSegment(): void {
    if (this.outboundFlightSegmentsArray.length + this.returnFlightSegmentsArray.length >= this.maxSegments) {
      this.error = `Maximum ${this.maxSegments} segments allowed`;
      return;
    }
    this.outboundFlightSegmentsArray.push(this.createFlightSegment({}));
  }

  // Add return segment
  addReturnSegment(): void {
    if (this.outboundFlightSegmentsArray.length + this.returnFlightSegmentsArray.length >= this.maxSegments) {
      this.error = `Maximum ${this.maxSegments} segments allowed`;
      return;
    }
    this.returnFlightSegmentsArray.push(this.createFlightSegment({}));
  }

  // Remove outbound segment (only if more than minimum)
  removeOutboundSegment(index: number): void {
    if (this.outboundFlightSegmentsArray.length <= this.minOutboundSegments) {
      this.error = 'At least one outbound segment is required';
      return;
    }
    this.outboundFlightSegmentsArray.removeAt(index);
  }

  // Remove return segment (only if more than minimum)
  removeReturnSegment(index: number): void {
    if (this.returnFlightSegmentsArray.length <= this.minReturnSegments) {
      this.error = 'At least one return segment is required';
      return;
    }
    this.returnFlightSegmentsArray.removeAt(index);
  }

  // Can remove outbound segment?
  canRemoveOutbound(index: number): boolean {
    return this.outboundFlightSegmentsArray.length > this.minOutboundSegments && index >= this.minOutboundSegments;
  }

  // Can remove return segment?
  canRemoveReturn(index: number): boolean {
    return this.returnFlightSegmentsArray.length > this.minReturnSegments && index >= this.minReturnSegments;
  }

  // Total segment count
  get totalSegments(): number {
    return this.outboundFlightSegmentsArray.length + this.returnFlightSegmentsArray.length;
  }

  // Can add more segments?
  get canAddSegment(): boolean {
    return this.totalSegments < this.maxSegments;
  }

  ngOnInit(): void {
    this.outboundFlightSegmentsArray.push(this.createFlightSegment());
    this.returnFlightSegmentsArray.push(this.createFlightSegment());
    this.loadHotels();
    this.loadExistingPackages();
  }

  loadHotels(): void {
    // Load hotels from your API
    this.packageService.getHotels().subscribe({
      next: (response: any) => {
        if (response.success) {
          this.allHotels = response.data;

          // Separate hotels by type
          this.makkahHotels = this.allHotels.filter(h => h.hotelType === 'Makkah');
          this.madinahHotels = this.allHotels.filter(h => h.hotelType === 'Madinah');

          // Set up listeners for hotel selection changes
          this.setupHotelChangeListeners();
        }
      },
      error: (error) => {
        console.error('Error loading hotels:', error);
      }
    });
  }

  setupHotelChangeListeners(): void {
    // Listen for Makkah hotel changes
    this.singlePackageForm.get('makkahHotel').valueChanges.subscribe(hotelName => {
      if (hotelName) {
        this.selectedMakkahHotel = this.makkahHotels.find(
          h => h.hotelName === hotelName || this.hotelNameMatch(h.hotelName, hotelName)
        ) || null;
      } else {
        this.selectedMakkahHotel = null;
      }

      // Update the hotel details in form
      this.updateMakkahHotelDetails();
    });

    // Listen for Madinah hotel changes
    this.singlePackageForm.get('madinahHotel').valueChanges.subscribe(hotelName => {
      if (hotelName) {
        this.selectedMadinahHotel = this.madinahHotels.find(
          h => h.hotelName === hotelName || this.hotelNameMatch(h.hotelName, hotelName)
        ) || null;
      } else {
        this.selectedMadinahHotel = null;
      }

      // Update the hotel details in form
      this.updateMadinahHotelDetails();
    });
  }

  // Helper to match hotel names (handles "OR SIMILAR" variations)
  hotelNameMatch(hotelTableName: string, selectedName: string): boolean {
    return selectedName.toUpperCase().includes(hotelTableName.toUpperCase()) ||
      hotelTableName.toUpperCase().includes(selectedName.toUpperCase());
  }

  updateMakkahHotelDetails(): void {
    if (this.selectedMakkahHotel) {
      this.singlePackageForm.patchValue({
        makkahHotelAddress: this.selectedMakkahHotel.hotelAddress,
        makkahHotelImage: this.selectedMakkahHotel.hotelImage,
        makkahHotelMapLocation: this.selectedMakkahHotel.hotelMapLocation
      }, { emitEvent: false });
    } else {
      this.singlePackageForm.patchValue({
        makkahHotelAddress: '',
        makkahHotelImage: '',
        makkahHotelMapLocation: ''
      }, { emitEvent: false });
    }
  }

  updateMadinahHotelDetails(): void {
    if (this.selectedMadinahHotel) {
      this.singlePackageForm.patchValue({
        madinahHotelAddress: this.selectedMadinahHotel.hotelAddress,
        madinahHotelImage: this.selectedMadinahHotel.hotelImage,
        madinahHotelMapLocation: this.selectedMadinahHotel.hotelMapLocation
      }, { emitEvent: false });
    } else {
      this.singlePackageForm.patchValue({
        madinahHotelAddress: '',
        madinahHotelImage: '',
        madinahHotelMapLocation: ''
      }, { emitEvent: false });
    }
  }

  // Format image URL
  getHotelImageUrl(imageName: string): string {
    if (!imageName) return '../../assets/hotels/hotel-placeholder.jpg';
    return `../../assets/hotels/hotels/${imageName}`;
  }

  loadExistingPackages(): void {
    this.packageService.getAllPackages().subscribe({
      next: (response) => {
        if (response.success) {
          this.existingPackages = response.umraPackagesList;
        }
      },
      error: (error) => {
        console.error('Error loading packages:', error);
      }
    });
  }

  // MODE SWITCHING
  setMode(mode: PackageMode): void {
    this.currentMode = mode;
    this.resetMessages();
  }

  // SINGLE PACKAGE SUBMISSION
  onSubmitSingle(): void {
    this.submitted = true;

    if (this.singlePackageForm.invalid) {
      return;
    }

    this.loading = true;
    this.resetMessages();

    const formData = this.singlePackageForm.value;

    // Extract flight segments
    const outboundFlights = formData.outboundFlights as UmraFlightSegmentProperties[];
    const returnFlights = formData.returnFlights as UmraFlightSegmentProperties[];

    // First outbound segment is the main departure
    const firstOutbound = outboundFlights[0];
    const lastOutbound = outboundFlights[outboundFlights.length - 1];

    // First return segment is the main return departure
    const firstReturn = returnFlights[0];
    const lastReturn = returnFlights[returnFlights.length - 1];

    const pkg: UmraPackageProperties = {
      id: 0,
      packageId: 0,
      packageTitle: formData.packageTitle,
      airlineName: firstOutbound.airlineName,
      routeStart: firstOutbound.routeStart,
      routeEnd: lastOutbound.routeEnd,
      departureRoute: `${firstOutbound.routeStart}-${lastOutbound.routeEnd}`,
      returnRoute: `${firstReturn.routeStart}-${lastReturn.routeEnd}`,
      outboundJourney: JSON.stringify(outboundFlights),
      returnJourney: JSON.stringify(returnFlights),
      travelDate: new Date(firstOutbound.travelDate).toISOString(),
      arrivalDate: new Date(firstReturn.travelDate).toISOString(),
      nightsCount: formData.nightsCount,
      makkahHotel: formData.makkahHotel,
      madinahHotel: formData.madinahHotel,
      priceSharing: parseFloat(formData.priceSharing) || 0,
      priceDouble: parseFloat(formData.priceDouble) || 0,
      priceTriple: parseFloat(formData.priceTriple) || 0,
      priceQuad: parseFloat(formData.priceQuad) || 0,
      availableSeats: formData.availableSeats,
      badgeText: formData.badgeText || 'Custom',
      groupCode: formData.groupCode || `CUSTOM-${Date.now()}`,
      packageCode: formData.packageCode || `CP-${Date.now()}`,
      provider: formData.provider,
      isActive: true,
      creationAgencyId: localStorage.getItem("AgencyId"),
      reservationAgencyId: '',
      creationUserId: parseInt(localStorage.getItem("UserId")),
      reservationUserId: 1,
      creationDate: new Date().toISOString(),
      offerStatus: 'Custom',
      approvalUserId: null,
      makkahHotelAddress: '',
      makkahHotelImage: '',
      makkahHotelMapLocation: '',
      madinahHotelAddress: '',
      madinahHotelImage: '',
      madinahHotelMapLocation: '',
      departureETD: firstOutbound.etd || '00:00',
      departureETA: lastOutbound.eta || '00:00',
      arrivalETD: firstReturn.etd || '00:00',
      arrivalETA: lastReturn.eta || '00:00',
      departureFligntNo: firstOutbound.flightNo,
      arrivalFligntNo: firstReturn.flightNo,
      discountPerPassenger: formData.discountPerPassenger || 0,
      serviceChargesPerPassenger: formData.serviceChargesPerPassenger || 0,
      customPackageId: '',
      providerType: 'Custom',
      supplierLetter: 'A',
      makkahZayarat: formData.makkahZayarat,
      madinahZayarat: formData.madinahZayarat,
      taifZayarat: formData.taifZayarat,
      markupOnSupplier: formData.serviceChargesPerPassenger || 0,
      discountOnSupplier: formData.discountPerPassenger || 0,
    };

    this.packageService.addCustomPackage(pkg).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = `Package added successfully! Package ID: ${response.packageId}`;
          this.singlePackageForm.reset({
            nightsCount: 14,
            availableSeats: 30,
            provider: 'Tushmit Travel & Tours'
          });
          this.submitted = false;
        } else {
          this.error = response.message || 'Failed to add package';
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error adding package:', error);
        this.error = 'Failed to add package. Please try again.';
        this.loading = false;
      }
    });
  }
  // Helper method to build route string
  private buildRouteString(segments: UmraFlightSegmentProperties[]): string {
    if (!segments || segments.length === 0) return '';

    const routePoints: string[] = [];

    segments.forEach((segment, index) => {
      if (index === 0) {
        // First segment: added both start and end
        routePoints.push(segment.routeStart);
        routePoints.push(segment.routeEnd);
      } else {
        // Subsequent segments: only added end (start is same as previous end)
        routePoints.push(segment.routeEnd);
      }
    });

    return routePoints.join('-');
  }
  // BATCH PACKAGE - Parse and Preview
  parseBatchData(): void {
    const batchData = this.batchPackageForm.get('batchData').value;

    if (!batchData.trim()) {
      this.error = 'Please enter batch data';
      return;
    }

    try {
      // Try to parse as JSON first
      let packages: any[];

      if (batchData.trim().startsWith('[')) {
        packages = JSON.parse(batchData);
      } else {
        // Parse CSV or tab-separated format
        packages = this.parseCSVFormat(batchData);
      }

      if (!packages || packages.length === 0) {
        this.error = 'No valid packages found in batch data';
        return;
      }

      this.batchPreviewData = packages.map((pkg, index) => ({
        index: index + 1,
        packageTitle: pkg.packageTitle || pkg.PackageTitle || `Custom Package ${index + 1}`,
        airlineName: pkg.airlineName || pkg.AirlineName || 'AirBlue',
        route: `${pkg.routeStart || pkg.RouteStart || 'LHE'} → ${pkg.routeEnd || pkg.RouteEnd || 'JED'}`,
        travelDate: pkg.travelDate || pkg.TravelDate || new Date().toISOString(),
        nightsCount: pkg.nightsCount || pkg.NightsCount || this.batchPackageForm.get('defaultNights').value,
        priceSharing: pkg.priceSharing || pkg.PriceSharing || 0,
        availableSeats: pkg.availableSeats || pkg.AvailableSeats || this.batchPackageForm.get('defaultSeats').value
      }));

      this.showBatchPreview = true;
      this.error = null;
    } catch (e) {
      console.error('Error parsing batch data:', e);
      this.error = 'Invalid batch data format. Please check your input.';
      this.showBatchPreview = false;
    }
  }

  private parseCSVFormat(data: string): any[] {
    const lines = data.split('\n').filter(line => line.trim());
    const packages: any[] = [];

    for (let i = 1; i < lines.length; i++) { // Skip header row
      const values = lines[i].split('\t').map(v => v.trim());
      if (values.length >= 6) {
        packages.push({
          packageTitle: values[0],
          airlineName: values[1],
          routeStart: values[2].split('-')[0] || 'LHE',
          routeEnd: values[2].split('-')[1] || 'JED',
          travelDate: values[3],
          nightsCount: parseInt(values[4]) || 14,
          priceSharing: parseFloat(values[5]) || 0,
          availableSeats: parseInt(values[6]) || 30
        });
      }
    }

    return packages;
  }

  // BATCH PACKAGE SUBMISSION
  submitBatchPackages(): void {
    if (this.batchPreviewData.length === 0) {
      this.error = 'No packages to submit';
      return;
    }

    this.loading = true;
    this.resetMessages();

    const defaultNights = this.batchPackageForm.get('defaultNights').value;
    const defaultSeats = this.batchPackageForm.get('defaultSeats').value;
    const defaultProvider = this.batchPackageForm.get('defaultProvider').value;

    const packages: UmraPackageProperties[] = this.batchPreviewData.map((item, index) => ({
      id:0,
      packageId: item.packageId,
      packageTitle: item.packageTitle,
      airlineName: item.airlineName,
      routeStart: item.route.split(' → ')[0] || 'LHE',
      routeEnd: item.route.split(' → ')[1] || 'JED',
      outboundJourney: '',
      returnJourney: '',
      departureRoute: '',
      returnRoute: item.returnRoute || item.route.split(' → ')[1] + ' → ' + item.route.split(' → ')[0],
      travelDate: new Date(item.travelDate).toISOString(),
      arrivalDate: new Date(item.arrivalDate).toISOString(),
      nightsCount: item.nightsCount || defaultNights,
      makkahHotel: 'TO BE CONFIRMED',
      madinahHotel: 'TO BE CONFIRMED',
      priceSharing: item.priceSharing || 0,
      priceDouble: item.priceSharing ? item.priceSharing * 0.9 : 0,
      priceTriple: item.priceSharing ? item.priceSharing * 1.1 : 0,
      priceQuad: item.priceSharing ? item.priceSharing * 1.2 : 0,
      availableSeats: item.availableSeats || defaultSeats,
      badgeText: 'Custom',
      groupCode: `BATCH-${Date.now()}`,
      packageCode: `BP-${Date.now()}-${index}`,
      provider: defaultProvider,
      isActive: true,
      creationAgencyId: 'WEB',
      reservationAgencyId: 'WEB',
      creationUserId: 1,
      reservationUserId: 1,
      creationDate: new Date().toISOString(),
      offerStatus: 'Custom',
      approvalUserId: null,
      makkahHotelAddress: '',
      makkahHotelImage: '',
      makkahHotelMapLocation: '',
      madinahHotelAddress: '',
      madinahHotelImage: '',
      madinahHotelMapLocation: '',
      departureETD: item.departureETD || '00:00',
      departureETA: item.departureETA || '00:00',
      arrivalETD: item.arrivalETD || '00:00',
      arrivalETA: item.arrivalETA || '00:00',
      departureFligntNo: item.departureFligntNo,
      arrivalFligntNo: item.arrivalFligntNo,
      discountPerPassenger: item.discountPerPassenger,
      serviceChargesPerPassenger: item.serviceChargesPerPassenger,
      customPackageId: '',
      providerType: 'Custom',
      supplierLetter: 'A',
      makkahZayarat: item.makkahZayarat,
      madinahZayarat: item.madinahZayarat,
      taifZayarat: item.taifZayarat,
      markupOnSupplier: item.serviceChargesPerPassenger || 0,
      discountOnSupplier: item.discountPerPassenger || 0
    }));

    this.packageService.addBatchPackages(packages).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = `${response.count} packages added successfully!`;
          this.batchPreviewData = [];
          this.showBatchPreview = false;
          this.batchPackageForm.reset({
            defaultNights: 14,
            defaultSeats: 30,
            defaultProvider: 'Custom'
          });
        } else {
          this.error = response.message || 'Failed to add batch packages';
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error adding batch packages:', error);
        this.error = 'Failed to add batch packages. Please try again.';
        this.loading = false;
      }
    });
  }

  // DUPLICATE PACKAGE
  onDuplicatePackage(): void {
    const packageId = this.duplicateForm.get('sourcePackageId').value;

    if (!packageId) {
      this.error = 'Please select a package to duplicate';
      return;
    }

    this.loading = true;
    this.resetMessages();

    this.packageService.duplicatePackage(packageId).subscribe({
      next: (response) => {
        if (response.success) {
          this.successMessage = `Package duplicated successfully! New Package ID: ${response.packageId}`;
          this.duplicateForm.reset();
        } else {
          this.error = response.message || 'Failed to duplicate package';
        }
        this.loading = false;
      },
      error: (error) => {
        console.error('Error duplicating package:', error);
        this.error = 'Failed to duplicate package. Please try again.';
        this.loading = false;
      }
    });
  }

  // HELPERS
  resetMessages(): void {
    this.successMessage = null;
    this.error = null;
  }

  get singleForm() {
    return this.singlePackageForm.controls;
  }

  get duplicateFormControls() {
    return this.duplicateForm.controls;
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'PKR',
      minimumFractionDigits: 0
    }).format(price);
  }
  getSelectedPackage(): UmraPackageProperties | null {
    const packageId = this.duplicateForm.get('sourcePackageId').value;
    if (!packageId) return null;
    return this.existingPackages.find(p => p.packageId === parseInt(packageId)) || null;
  }
}
